import { useEffect, useMemo, useRef } from 'react'

import { ColDef, IServerSideGetRowsParams } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'

import { useRegisterAgGridRefEffect } from '@contexts/grid-ref-context'
import { toClassName } from '@utils/string-utils'

import StatusBar from '@components/ag-grid/status-bar'
import { PageBuilderEnum } from '@components/financial/types'

import useAuth from '@hooks/useAuth'
import { useLatestRef } from '@hooks/useLatestRef'
import useRouteBasedID from '@hooks/useRouteBasedID'

import { useDispatch, usePageDispatch, usePageSelector } from '@store/index'
import {
  resetDataExportAsyncMode,
  selectDisplayExpansionControls,
  setDataExportAsyncMode,
  setDisplayExpansionControls
} from '@store/slices/action-bar'
import { modifyFilterModel, modifySortModel } from '@store/slices/component/table-parameters'

import { useExpandLevelHandler } from '@pages/financial-statements/hooks/expand-level-handler'

import AgGrid from 'components/ag-grid'
import ComponentWidget from 'components/cards/component-widget'
import { UserProfile } from 'types/auth'

import {
  QUERY_KEY_PREFIX as RESULT_QUERY_KEY_PREFIX,
  fetchComponentResults
} from '../queries/fetch-component-result'
import {
  QUERY_KEY_PREFIX as STAGED_QUERY_KEY_PREFIX,
  fetchComponentStagedData
} from '../queries/fetch-component-staged-data'
import { IComponentResultsData } from '../types'
import { IComponentSorts } from '../types/component-types'
import { IInitialFilterModelConfig, ITableColumn, ITableConfig } from '../types/table-builder-types'
import { ComponentPreviewProps } from '../viewer/types'
import { DefaultFilterModel } from './default-filter-model'
import { useBroadcastValue } from './hooks/use-broadcast-value'
import { useColDefs } from './hooks/use-col-defs'
import { mergeFilterModelWithConfig } from './utils'

const SYNC_PAGINATED_EXPORT_LIMIT = 30_000

class ServerSideDatasource {
  firstLoad = true

  constructor(
    private user: UserProfile | null | undefined,
    private client: QueryClient,
    private queryBodyRef: any,
    private businessIdRef: React.MutableRefObject<number | undefined>,
    private componentId: number,
    private fromViewer: boolean,
    private selectRowOnDataRefreshedRef: React.MutableRefObject<() => void>,
    private initialFilterModelConfigRef: React.MutableRefObject<
      IInitialFilterModelConfig | undefined
    >
  ) {}

  async getRows(params: IServerSideGetRowsParams) {
    const offset = params.request.startRow || 0
    try {
      const response = this.fromViewer
        ? await this.fetchResultsData(offset)
        : await this.fetchStagedData(offset)

      const { results, results_count } = response
      const rowCount = results_count || results?.length || 0
      params.success({ rowData: results, rowCount })
      setTimeout(() => this.selectRowOnDataRefreshed(), 0)
    } catch (error) {
      params.fail()
    }

    if (this.firstLoad) {
      params.api.setFilterModel(this.initialFilterModel)
      params.api.applyColumnState({
        state: this.initialSortModel || [],
        defaultState: { sort: null }
      })
      this.firstLoad = false
    }
  }

  fetchStagedData = async (offset: number) => {
    const queryBody = _.cloneDeep(this.queryBody)
    if (queryBody.parameters) {
      queryBody.parameters.offset = offset
    }

    let response = this.client.getQueryData([
      STAGED_QUERY_KEY_PREFIX,
      this.businessIdRef.current,
      queryBody
    ]) as IComponentResultsData

    if (!response) {
      response = await this.client.fetchQuery({
        queryKey: [STAGED_QUERY_KEY_PREFIX, this.businessIdRef.current, queryBody],
        queryFn: () => fetchComponentStagedData(queryBody, this.businessIdRef.current as number)
      })
    }

    return response
  }

  fetchResultsData = async (offset: number) => {
    const parameters = _.cloneDeep(this.parameters)
    parameters.offset = offset

    let response = this.client.getQueryData([
      RESULT_QUERY_KEY_PREFIX,
      this.componentId,
      this.businessIdRef.current,
      parameters
    ]) as IComponentResultsData

    if (!response) {
      response = await this.client.fetchQuery({
        queryKey: [
          RESULT_QUERY_KEY_PREFIX,
          this.componentId,
          this.businessIdRef.current,
          parameters
        ],
        queryFn: () =>
          fetchComponentResults(this.componentId, this.businessIdRef.current as number, parameters)
      })
    }

    return response
  }

  get queryBody() {
    return this.queryBodyRef.current
  }

  get initialFilterModel() {
    const initialFilterModelConfig = this.initialFilterModelConfigRef.current
    const final = mergeFilterModelWithConfig(
      _.get(this.parameters, 'filter_model', null),
      this.user,
      initialFilterModelConfig
    )
    return final
  }

  get initialSortModel() {
    return _.get(this.parameters, 'sort_model', [])
  }

  get parameters() {
    return _.get(this.queryBody, 'parameters', {})
  }

  get selectRowOnDataRefreshed() {
    return this.selectRowOnDataRefreshedRef.current
  }
}

export function ServerSideTable(props: ComponentPreviewProps) {
  const { data, title, isFetching, gridOptions, queryBody, fromViewer, componentId } = props
  const dispatch = useDispatch()
  const pageDispatch = usePageDispatch()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const businessIdRef = useRef(user?.business_id)
  const queryBodyRef = useLatestRef(queryBody)
  const displayExpansionControls = usePageSelector(selectDisplayExpansionControls)
  const routeId = useRouteBasedID()

  const gridRef = useRef<AgGridReact | null>(null)
  useRegisterAgGridRefEffect(gridRef, PageBuilderEnum.PageBuilder)
  const config = useMemo(() => _.get(data, 'config') as ITableConfig, [data])
  const paginated = _.get(data, 'paginated', false)
  const initialFilterModelConfig = _.get(config, 'initialFilterModelConfig')
  const initialFilterModelConfigRef = useLatestRef(initialFilterModelConfig)

  const columns = useMemo(() => _.get(config, 'columns', []) as ITableColumn[], [config])
  const columnDefs = useColDefs({
    columns,
    gridRef,
    externalFiltersChanged: 0,
    showFilters: true,
    paginated: paginated
  })

  const { getBroadcastGridOptions, broadcastClassNames, selectRowOnDataRefreshed } =
    useBroadcastValue({ columns, gridRef })
  const selectRowOnDataRefreshedRef = useLatestRef(selectRowOnDataRefreshed)

  useEffect(() => {
    if (queryBody) {
      gridRef.current?.api?.refreshServerSide({ purge: true })
    }
  }, [queryBody])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      filter: true,
      floatingFilter: true,
      sortable: true,
      suppressHeaderMenuButton: true
    }),
    []
  )

  const rowCount = gridRef.current?.api?.getDisplayedRowCount() || 0

  useEffect(() => {
    if (paginated) {
      if (rowCount > SYNC_PAGINATED_EXPORT_LIMIT) {
        pageDispatch(setDataExportAsyncMode({}))
      } else {
        pageDispatch(resetDataExportAsyncMode({}))
      }
    }
  }, [paginated, rowCount, pageDispatch])

  const {
    onRowDataUpdated: onRowDataUpdatedInternal,
    onRowGroupOpened: onRowGroupOpenedInternal,
    onFilterChanged
  } = useExpandLevelHandler({
    persistExpandedGroups: true,
    combinedId: routeId,
    gridRef
  })

  return (
    <ComponentWidget
      loading={isFetching}
      title={title}
      titleSecondary={<DefaultFilterModel showSettings={!props.fromViewer} />}
    >
      <div className='size-full pb-3'>
        <AgGrid
          // debug
          id={toClassName(`${props.componentId}-${title}`)}
          className={broadcastClassNames}
          style={{ height: '100%' }}
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          removePivotHeaderRowWhenSingleValueColumn
          suppressServerSideFullWidthLoadingRow
          rowModelType='serverSide'
          rowBuffer={0}
          cacheBlockSize={500}
          cacheOverflowSize={5}
          maxConcurrentDatasourceRequests={1}
          infiniteInitialRowCount={500}
          maxBlocksInCache={10}
          onModelUpdated={(params) => {
            if (!displayExpansionControls) {
              pageDispatch(setDisplayExpansionControls({ displayExpansionControls: true }))
            }
            onRowDataUpdatedInternal(params)
          }}
          onRowGroupOpened={onRowGroupOpenedInternal}
          onGridReady={(event) => {
            const dataSource = new ServerSideDatasource(
              user,
              queryClient,
              queryBodyRef,
              businessIdRef,
              componentId,
              fromViewer || false,
              selectRowOnDataRefreshedRef,
              initialFilterModelConfigRef
            )
            event.api.setGridOption('serverSideDatasource', dataSource)
          }}
          onFilterChanged={(event) => {
            dispatch(
              modifyFilterModel({
                filterModel: event.api.getFilterModel(),
                componentId: `${props.componentId}`
              })
            )
            onFilterChanged(event)
          }}
          onSortChanged={(event) => {
            const sortModel = _.map(
              _.filter(event.api.getColumnState(), (s) => !!s.sort),
              (s) => _.pick(s, ['colId', 'sort', 'sortIndex'])
            ) as IComponentSorts
            dispatch(modifySortModel({ sortModel, componentId: `${props.componentId}` }))
          }}
          allowDragFromColumnsToolPanel
          suppressDragLeaveHidesColumns
          autoSizeStrategy={{ type: 'fitCellContents' }}
          persistColumnState
          {...gridOptions}
          {...getBroadcastGridOptions()}
          statusBar={{
            statusPanels: [
              {
                statusPanel: StatusBar,
                statusPanelParams: { aggFuncs: ['sum', 'avg', 'count'] }
              }
            ]
          }}
        />
      </div>
    </ComponentWidget>
  )
}
